import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { FISDataCategory } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    DataSourceLookup,
    InputNullableInt,
    ValidatedInt,
    InputText,
    ValidatedText,
    InputNullableFloat,
    ValidatedFloat,
} from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class FISDataCategoryGuiDef extends GuiDef<FISDataCategory> {
    constructor() {
        super('FISDataCategory');

        const EventCommon = storeToRefs(useEventCommonStore());
        const CategoriesSource = computed(() => {
            return EventCommon.CategoriesAsSelectLine.value;
        });

        const ID = new FieldDef<FISDataCategory>();
        ID.Name = 'ID';
        ID.FieldName = 'ID';
        ID.DefinedFor = 'Both';
        ID.TranslationKey = 'fisdatacategory.id';
        ID.Editable = false;
        ID.Filterable = false;
        ID.Sortable = true;
        ID.CssClasses = '';
        ID.value = (fisdatacategory) => fisdatacategory.ID;
        ID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        ID.CellRenderer = markRaw(DataSourceLookup);
        ID.CellRendererProps = { datasource: { mapsource: CategoriesSource }, readonly: true, hideCaret: true };
        ID.ExportValueFormatter = (obj: FISDataCategory) => {
            return CategoriesSource.value.get(obj.ID)?.text;
        };
        ID.Validator = null;
        ID.SortDirection = undefined;
        ID.cellClassRules = undefined;
        ID.MinWidth = undefined;
        ID.MaxWidth = undefined;
        ID.Tooltip = null;
        this.Fields.push(ID);

        const Codex = new FieldDef<FISDataCategory>();
        Codex.Name = 'Codex';
        Codex.FieldName = 'Codex';
        Codex.DefinedFor = 'Both';
        Codex.TranslationKey = 'fisdatacategory.codex';
        Codex.Editable = true;
        Codex.Filterable = false;
        Codex.Sortable = true;
        Codex.CssClasses = '';
        Codex.value = (fisdatacategory) => fisdatacategory.Codex;
        Codex.setValue = (fisdatacategory, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatacategory.Codex = value;
        };
        Codex.DetailEditor = markRaw(InputNullableInt);
        Codex.DetailEditorProps = {};
        Codex.CellEditor = markRaw(InputNullableInt);
        Codex.CellEditorProps = {};
        Codex.CellRenderer = markRaw(ValidatedInt);
        Codex.CellRendererProps = {};
        Codex.Validator = null;
        Codex.SortDirection = undefined;
        Codex.cellClassRules = undefined;
        Codex.MinWidth = undefined;
        Codex.MaxWidth = undefined;
        Codex.Tooltip = null;
        this.Fields.push(Codex);

        const Finalecodex = new FieldDef<FISDataCategory>();
        Finalecodex.Name = 'Finalecodex';
        Finalecodex.FieldName = 'Finalecodex';
        Finalecodex.DefinedFor = 'Both';
        Finalecodex.TranslationKey = 'fisdatacategory.finalecodex';
        Finalecodex.Editable = true;
        Finalecodex.Filterable = false;
        Finalecodex.Sortable = true;
        Finalecodex.CssClasses = '';
        Finalecodex.value = (fisdatacategory) => fisdatacategory.Finalecodex;
        Finalecodex.setValue = (fisdatacategory, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatacategory.Finalecodex = value;
        };
        Finalecodex.DetailEditor = markRaw(InputNullableInt);
        Finalecodex.DetailEditorProps = {};
        Finalecodex.CellEditor = markRaw(InputNullableInt);
        Finalecodex.CellEditorProps = {};
        Finalecodex.CellRenderer = markRaw(ValidatedInt);
        Finalecodex.CellRendererProps = {};
        Finalecodex.Validator = null;
        Finalecodex.SortDirection = undefined;
        Finalecodex.cellClassRules = undefined;
        Finalecodex.MinWidth = undefined;
        Finalecodex.MaxWidth = undefined;
        Finalecodex.Tooltip = null;
        this.Fields.push(Finalecodex);

        const Fistype = new FieldDef<FISDataCategory>();
        Fistype.Name = 'Fistype';
        Fistype.FieldName = 'Fistype';
        Fistype.DefinedFor = 'Both';
        Fistype.TranslationKey = 'fisdatacategory.fistype';
        Fistype.Editable = true;
        Fistype.Filterable = true;
        Fistype.Sortable = true;
        Fistype.CssClasses = '';
        Fistype.value = (fisdatacategory) => fisdatacategory.Fistype;
        Fistype.setValue = (fisdatacategory, value) => (fisdatacategory.Fistype = value);
        Fistype.DetailEditor = markRaw(InputText);
        Fistype.DetailEditorProps = {};
        Fistype.CellEditor = markRaw(InputText);
        Fistype.CellEditorProps = {};
        Fistype.CellRenderer = markRaw(ValidatedText);
        Fistype.CellRendererProps = {};
        Fistype.Validator = null;
        Fistype.SortDirection = undefined;
        Fistype.cellClassRules = undefined;
        Fistype.MinWidth = undefined;
        Fistype.MaxWidth = undefined;
        Fistype.Tooltip = null;
        this.Fields.push(Fistype);

        const Fiskategori = new FieldDef<FISDataCategory>();
        Fiskategori.Name = 'Fiskategori';
        Fiskategori.FieldName = 'Fiskategori';
        Fiskategori.DefinedFor = 'Both';
        Fiskategori.TranslationKey = 'fisdatacategory.fiskategori';
        Fiskategori.Editable = true;
        Fiskategori.Filterable = true;
        Fiskategori.Sortable = true;
        Fiskategori.CssClasses = '';
        Fiskategori.value = (fisdatacategory) => fisdatacategory.Fiskategori;
        Fiskategori.setValue = (fisdatacategory, value) => (fisdatacategory.Fiskategori = value);
        Fiskategori.DetailEditor = markRaw(InputText);
        Fiskategori.DetailEditorProps = {};
        Fiskategori.CellEditor = markRaw(InputText);
        Fiskategori.CellEditorProps = {};
        Fiskategori.CellRenderer = markRaw(ValidatedText);
        Fiskategori.CellRendererProps = {};
        Fiskategori.Validator = null;
        Fiskategori.SortDirection = undefined;
        Fiskategori.cellClassRules = undefined;
        Fiskategori.MinWidth = undefined;
        Fiskategori.MaxWidth = undefined;
        Fiskategori.Tooltip = null;
        this.Fields.push(Fiskategori);

        const Minpenalty = new FieldDef<FISDataCategory>();
        Minpenalty.Name = 'Minpenalty';
        Minpenalty.FieldName = 'Minpenalty';
        Minpenalty.DefinedFor = 'Both';
        Minpenalty.TranslationKey = 'fisdatacategory.minpenalty';
        Minpenalty.Editable = true;
        Minpenalty.Filterable = true;
        Minpenalty.Sortable = true;
        Minpenalty.CssClasses = '';
        Minpenalty.value = (fisdatacategory) => fisdatacategory.Minpenalty;
        Minpenalty.setValue = (fisdatacategory, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatacategory.Minpenalty = value;
        };
        Minpenalty.DetailEditor = markRaw(InputNullableFloat);
        Minpenalty.DetailEditorProps = {};
        Minpenalty.CellEditor = markRaw(InputNullableFloat);
        Minpenalty.CellEditorProps = {};
        Minpenalty.CellRenderer = markRaw(ValidatedFloat);
        Minpenalty.CellRendererProps = {};
        Minpenalty.Validator = null;
        Minpenalty.SortDirection = undefined;
        Minpenalty.cellClassRules = undefined;
        Minpenalty.MinWidth = undefined;
        Minpenalty.MaxWidth = undefined;
        Minpenalty.Tooltip = null;
        this.Fields.push(Minpenalty);

        const Fvalue = new FieldDef<FISDataCategory>();
        Fvalue.Name = 'Fvalue';
        Fvalue.FieldName = 'Fvalue';
        Fvalue.DefinedFor = 'Both';
        Fvalue.TranslationKey = 'fisdatacategory.fvalue';
        Fvalue.Editable = true;
        Fvalue.Filterable = false;
        Fvalue.Sortable = true;
        Fvalue.CssClasses = '';
        Fvalue.value = (fisdatacategory) => fisdatacategory.Fvalue;
        Fvalue.setValue = (fisdatacategory, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatacategory.Fvalue = value;
        };
        Fvalue.DetailEditor = markRaw(InputNullableInt);
        Fvalue.DetailEditorProps = {};
        Fvalue.CellEditor = markRaw(InputNullableInt);
        Fvalue.CellEditorProps = {};
        Fvalue.CellRenderer = markRaw(ValidatedInt);
        Fvalue.CellRendererProps = {};
        Fvalue.Validator = null;
        Fvalue.SortDirection = undefined;
        Fvalue.cellClassRules = undefined;
        Fvalue.MinWidth = undefined;
        Fvalue.MaxWidth = undefined;
        Fvalue.Tooltip = null;
        this.Fields.push(Fvalue);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Categories']);
    }
}
